// Household pets, and the animals a caregiver cannot work around.
//
// Families answer "Do you have any household pets?" on the intake form and
// the family referral; the answer lives on intake_submissions / leads and is
// carried to the client. Staff record pet_restrictions. Assigning a caregiver
// whose restrictions meet the home's pets warns (petConflict) — it does not
// block: a coordinator may know the dog is kept in another room.
//
// household_pets is null until the family has been asked. That is "Not
// asked", never "No pets".

export const PET_OPTIONS = ['none', 'dog', 'cat', 'other'];

export const PET_LABELS = {
  en: { none: 'No pets', dog: 'Dog', cat: 'Cat', other: 'Other' },
  es: { none: 'No tengo mascotas', dog: 'Perro', cat: 'Gato', other: 'Otro' },
};

export const PET_COPY = {
  en: {
    section: 'Household',
    question: 'Do you have any household pets?',
    hint: 'Select all that apply. This helps us place the right nurse with your family.',
    otherLabel: 'Please tell us exactly what animals you have',
    otherPlaceholder: 'e.g. 2 rabbits, 1 parrot',
    notesLabel: 'Anything the nurse should know? (optional)',
    notesPlaceholder: 'e.g. large dog, kept in a separate room during visits',
    required: 'Please tell us whether you have household pets.',
    otherRequired: 'Please tell us exactly what animals you have.',
  },
  es: {
    section: 'Hogar',
    question: '¿Tiene mascotas en el hogar?',
    hint: 'Seleccione todas las que apliquen. Esto nos ayuda a asignar la enfermera adecuada a su familia.',
    otherLabel: 'Indique exactamente qué animales tiene',
    otherPlaceholder: 'p. ej. 2 conejos, 1 loro',
    notesLabel: '¿Algo que la enfermera deba saber? (opcional)',
    notesPlaceholder: 'p. ej. perro grande, se queda en otra habitación durante las visitas',
    required: 'Indíquenos si tiene mascotas en el hogar.',
    otherRequired: 'Indique exactamente qué animales tiene.',
  },
};

/** Ticking "No pets" clears the animals; ticking an animal clears "No pets". */
export const togglePet = (current, key) => {
  const list = current || [];
  if (list.includes(key)) return list.filter(k => k !== key);
  if (key === 'none') return ['none'];
  return [...list.filter(k => k !== 'none'), key];
};

/** Error message key for the form, or null when the answer is complete. */
export const petAnswerError = (pets, other) => {
  if (!pets || pets.length === 0) return 'required';
  if (pets.includes('other') && !String(other || '').trim()) return 'otherRequired';
  return null;
};

/** The three columns to store, trimmed. */
export const petColumns = (pets, other, notes) => ({
  household_pets: pets && pets.length ? pets : null,
  household_pets_other: pets?.includes('other') ? (String(other || '').trim() || null) : null,
  household_pets_notes: String(notes || '').trim() || null,
});

/** "Dog, Cat, 2 rabbits" · "No pets" · "Not asked". */
export const petSummary = (record) => {
  const pets = record?.household_pets;
  if (!pets || pets.length === 0) return 'Not asked';
  if (pets.includes('none')) return 'No pets';
  return pets.map(k => (k === 'other' ? (record.household_pets_other || 'Other') : PET_LABELS.en[k])).join(', ');
};

export const hasPets = (record) => !!record?.household_pets?.some(k => k !== 'none');

// ── Staff side ──────────────────────────────────────────────────────────────
export const RESTRICTION_OPTIONS = [
  ['dog', 'Dogs'],
  ['cat', 'Cats'],
  ['any', 'Any animal (e.g. severe allergy)'],
];

/**
 * Why this caregiver should not be sent to this home, or null.
 * 'any' conflicts with any pet at all, including "other".
 */
export const petConflict = (staff, client) => {
  const restrictions = staff?.pet_restrictions || [];
  if (!restrictions.length || !hasPets(client)) return null;
  const pets = client.household_pets;
  const hits = restrictions.includes('any')
    ? pets.filter(k => k !== 'none')
    : pets.filter(k => restrictions.includes(k));
  if (!hits.length) return null;
  const home = petSummary(client);
  const who = staff.full_name || [staff.first_name, staff.last_name].filter(Boolean).join(' ') || 'This caregiver';
  return `${who} cannot work around ${restrictions.includes('any') ? 'animals' : hits.map(k => PET_LABELS.en[k].toLowerCase() + 's').join(' or ')}; this home has: ${home}.`;
};
